// libraries
import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import { Form, FormInstance, Input, Select, Button, notification } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import * as Colors from '@ant-design/colors';

// services
import * as UserService from '../../../../services/bb-api/user';
import * as MunicipalityService from '../../../../services/bb-api/municipality';
import * as UniverseService from '../../../../services/bb-api/universe';

// utils
import { txt } from '../../../../locales/i18n';
import { useDeviceSize } from '../../../../hooks/device-size';

const startYear: number = 1920;
const currentYear: number = moment().year();
const birthYears: number[] = [...Array(currentYear - startYear)].map(
	(_, index: number) => index + startYear
);

const countries = ['denmark', 'sweden', 'germany'];

export function UserCreatePage(): JSX.Element {
	const [loading, setLoading] = useState<boolean>(false);
	const [municipalities, setMunicipalities] = useState<any[]>([]);
	const [universes, setUniverses] = useState<any[]>([]);
	const [emailValidationTimeout, setEmailValidationTimeout] = useState<NodeJS.Timeout | null>(
		null
	);
	const [emailValidateStatus, setEmailValidateStatus] = useState<
		'validating' | 'success' | 'error' | ''
	>('');

	const cancelButtonRef = useRef<HTMLButtonElement>(null);
	const isMobileDevice: boolean = useDeviceSize();

	const formRef = useRef<FormInstance>(null);
	const municipalityInputRef = useRef<HTMLSelectElement>(null);
	const usernameRef = useRef<any>(null);
	const countryRef = useRef<any>(null);
	const emailRef = useRef<any>(null);
	const history = useHistory();

	useEffect(() => {
		async function getFormResources(): Promise<void> {
			setLoading(true);

			try {
				const municipalities: Record<string, any>[] = await MunicipalityService.query();
				const municipalitiesOptions: any[] = municipalities.map(
					({ id, name }: Record<string, any>) => {
						return { label: name, value: id };
					}
				);

				setMunicipalities(municipalitiesOptions);

				const universes: Record<string, any>[] = await UniverseService.query();
				const universesOptions: any[] = universes.map(
					({ id, title }: Record<string, any>) => {
						return { label: title, value: id };
					}
				);

				setUniverses(universesOptions);
			} catch (error) {
				const message: string = (error as Error).name;
				const description: string = (error as Error).message;
				notification.error({ message, description });
			}

			setLoading(false);
		}

		getFormResources();
		municipalityInputRef.current?.focus();
		cancelButtonRef.current?.setAttribute('tabindex', '-1');
	}, []);

	async function handleCreate(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		if (emailValidateStatus === 'validating') {
			notification.info({ message: txt('notification.validating email') });
			return;
		}

		const emailErrors: string[] = form.getFieldError('email');
		const birthYearErrors: string[] = form.getFieldError('birth_year');
		const errors: string[] = [...birthYearErrors, ...emailErrors];

		if (errors.length !== 0) {
			for (const error of errors) {
				notification.error({ message: error });
			}

			return;
		}

		setLoading(true);

		try {
			await UserService.create(values);
			notification.success({ message: txt('notification.create successful') });
			form.resetFields(['username', 'birth_year', 'email']);
		} catch (error) {
			if ((error as Error).message === 'EMAIL_ALREADY_TAKEN') {
				form.setFields([{ name: 'email', errors: [txt('notification.email occupied')] }]);
				notification.error({ message: txt('notification.email occupied') });
				setLoading(false);
				return;
			}

			if ((error as Error).message === 'UNDER_AGE') {
				form.setFields([
					{ name: 'birth_year', errors: [txt('notification.user to young')] },
				]);
				notification.error({ message: txt('notification.user to young') });
				setLoading(false);
				return;
			}

			notification.error({ message: txt('notification.create failed') });
		}

		setLoading(false);
		municipalityInputRef.current?.focus();
	}

	async function handleValueChange(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		if (values.municipality_id !== undefined) {
			let value = undefined;

			switch (values.municipality_id) {
				// aarhus
				case 3: {
					value = 12; // Studiebobler - AU
					break;
				}

				// herlev
				case 35: {
					value = 9; // Herlevbobler
					break;
				}
			}

			form.setFields([{ name: 'universeId', value }]);
		}

		if (values.birth_year !== undefined) {
			form.setFields([{ name: 'birth_year', errors: [] }]);
			return;
		}

		if (values.email !== undefined) {
			if (emailValidationTimeout !== null) clearTimeout(emailValidationTimeout);
			form.setFields([{ name: 'email', errors: [] }]);

			if (values.email === '') return setEmailValidateStatus('');
			setEmailValidateStatus('validating');

			const timeout: NodeJS.Timeout = setTimeout(async () => {
				const emailValidation: boolean = await UserService.validateEmail(values.email);
				setEmailValidateStatus(emailValidation ? 'success' : 'error');
				const emailErrorMessage: string[] = emailValidation
					? []
					: [txt('notification.email occupied')];
				form.setFields([{ name: 'email', errors: emailErrorMessage }]);
			}, 500);

			setEmailValidationTimeout(timeout);
		}
	}

	function handleBirthYearSearch(value: string) {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;
		if (value === '') return;

		const numericValue: number = parseInt(value);

		if (isNaN(numericValue)) {
			form.setFields([
				{ name: 'birth_year', value, errors: [txt('notification.invalid birth year')] },
			]);
			return;
		}

		if (numericValue < startYear || currentYear < numericValue) {
			form.setFields([
				{
					name: 'birth_year',
					value,
					errors: [txt('notification.birth year not accepted')],
				},
			]);
			return;
		}

		form.setFields([{ name: 'birth_year', value, errors: [] }]);
	}

	return (
		<main style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
			<StyledForm
				ref={formRef}
				onFinish={handleCreate}
				initialValues={{ municipality_id: 1, birth_year: 2000, country: 'denmark' }}
				labelCol={{ span: 3 }}
				isMobileDevice={isMobileDevice}
				onValuesChange={handleValueChange}
			>
				<Form.Item label={txt('global.municipality')} name="municipality_id">
					<Select
						loading={loading}
						showSearch
						optionFilterProp="label"
						dropdownMatchSelectWidth={false}
						options={municipalities}
						ref={municipalityInputRef}
						onSelect={() => usernameRef.current?.focus()}
					/>
				</Form.Item>

				<Form.Item label={txt('global.name')} name="username">
					<Input required ref={usernameRef} />
				</Form.Item>

				<Form.Item label={txt('global.birth year')} name="birth_year">
					<Select
						loading={loading}
						showSearch
						dropdownMatchSelectWidth={false}
						autoClearSearchValue={false}
						onSearch={handleBirthYearSearch}
						onSelect={() => emailRef.current?.focus()}
					>
						{birthYears.map((birthYear: number) => (
							<Select.Option key={birthYear} value={birthYear}>
								{birthYear}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="Email"
					name="email"
					hasFeedback
					validateStatus={emailValidateStatus}
				>
					<Input type="email" ref={emailRef} required />
				</Form.Item>

				<Form.Item label={txt('global.universe')} name="universeId">
					<Select
						loading={loading}
						showSearch
						optionFilterProp="label"
						dropdownMatchSelectWidth={false}
						options={universes}
						allowClear
						onSelect={() => countryRef.current?.focus()}
					/>
				</Form.Item>

				<Form.Item label={txt('global.country')} name="country">
					<Select loading={loading} dropdownMatchSelectWidth={false} ref={countryRef}>
						{countries.map((country) => (
							<Select.Option key={country} value={country}>
								{txt('countries.' + country)}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label=" " colon={false}>
					<div className="form-action-container">
						<Button
							type="primary"
							htmlType="submit"
							size={isMobileDevice ? 'large' : 'middle'}
							loading={loading}
						>
							{txt('global.create')} <CheckOutlined />
						</Button>

						<Button
							type="ghost"
							ref={cancelButtonRef}
							size={isMobileDevice ? 'large' : 'middle'}
							onClick={() => history.goBack()}
						>
							{txt('global.cancel')}
						</Button>
					</div>
				</Form.Item>
			</StyledForm>
		</main>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	display: flex;
	flex-direction: column;
	padding: 2rem;
	width: inherit;
	max-width: 650px;

	.form-action-container {
		display: flex;
		gap: 1em;
		justify-content: space-between;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		button[type='submit'] {
			border-color: ${Colors.green.primary};
			background-color: ${Colors.green.primary};
		}
	}
`;
